/**
 * OnboardingTrialPage — Step 4 (45-Day Free Trial) of the restaurant
 * onboarding wizard.
 *
 * iter141 (Jul 2026) — Owner spec: financial steps must complete BEFORE
 * publishing. The trial activation used to happen INSIDE `/onboarding/setup`
 * (triggered by a 402 on Publish → inline card → Stripe Checkout). Now it
 * lives here as its own dedicated step, between Stripe Connect and the
 * Restaurant Setup page. Everything below reuses existing plumbing:
 *
 *   • POST /api/billing/create-checkout-session — unchanged. Same
 *     endpoint the old inline card called. Returns a Stripe-hosted
 *     Checkout URL. We hard-navigate the tab so Stripe can host its
 *     own PCI page.
 *   • Stripe return URL — unchanged. Backend redirects Stripe to
 *     `/onboarding/setup?activated=1&session_id=…` on success. The
 *     setup page's existing `?activated=1` handler now just shows a
 *     success toast (see OnboardingSetupPage iter141).
 *   • GET /api/onboarding/state — reused to detect a restaurant whose
 *     trial is already active. Skips forward automatically.
 *
 * ZERO backend changes, ZERO Stripe integration changes, ZERO changes
 * to the trial / subscription business logic. Only the sequence of
 * screens moved.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, ArrowRight, Loader2, Check, ShieldCheck, Clock } from 'lucide-react';
import { toast } from 'sonner';
import OnboardingShell from '../components/OnboardingShell';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const OnboardingTrialPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth() || {};
  const [starting, setStarting] = useState(false);
  const [checking, setChecking] = useState(true);
  // iter144 — Stepper clicks pass `?edit=1` to signal that the owner
  // explicitly wants to view / review this step (even if they've
  // already completed it in a previous session). Without this, an
  // owner whose trial is already active would be auto-redirected
  // forward, defeating the point of clicking the Step 4 stepper.
  const editMode = searchParams.get('edit') === '1';

  useEffect(() => {
    if (!token) {
      navigate('/login?role=restaurant&next=/onboarding/trial');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // iter144 — Explicit stepper click → render the Trial page as
        // a review/preview so the owner can see what they activated.
        // Skip the "already active → skip forward" and "Stripe not
        // done → bounce back" auto-redirects entirely.
        if (editMode) return;

        const s = await axios.get(`${API}/api/onboarding/state`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const status = (s.data?.subscription_status || '').toLowerCase();
        const stripeOk = !!s.data?.checklist?.stripe_connected;

        // Trial already active — the owner completed Step 4 in a
        // previous session (or via a return trip). Skip forward.
        if (['active', 'trialing'].includes(status)) {
          navigate('/onboarding/setup', { replace: true });
          return;
        }
        // Stripe Connect not done yet — bounce back to Step 3 so the
        // stepper stays in strict order.
        if (!stripeOk) {
          navigate('/onboarding/stripe-connect', { replace: true });
          return;
        }
      } catch {
        /* let the page render; user can still start the trial. */
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, navigate, editMode]);

  const onActivateTrial = async () => {
    setStarting(true);
    try {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const res = await axios.post(
        `${API}/api/billing/create-checkout-session`,
        { origin_url: origin },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const url = res?.data?.url;
      if (!url) throw new Error('No checkout URL returned');
      window.location.href = url;
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
          'Could not start the trial activation. Please try again in a moment.',
      );
      setStarting(false);
    }
  };

  return (
    <OnboardingShell
      currentStep="subscribe"
      backFallback="/onboarding/stripe-connect"
      contentMaxWidth="max-w-2xl"
      testid="onboarding-trial-page"
    >
      <div className="pt-6 pb-16">
        <div className="text-center max-w-xl mx-auto mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-yellow-500/40 bg-yellow-500/10 mb-4">
            <Sparkles className="w-3.5 h-3.5 text-yellow-300" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-yellow-300 font-bold">
              Step 4 · Activate 45-Day Free Trial
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-white">
            Activate your 45-day free trial.
          </h1>
          <p className="mt-3 text-[14.5px] text-white/60 leading-relaxed">
            Add your payment method now so your subscription is ready when your setup goes live. No charges for the first 45 days — cancel anytime from your dashboard.
          </p>
        </div>

        <div
          data-testid="onboarding-trial-card"
          className="rounded-3xl border border-yellow-500/35 bg-gradient-to-br from-yellow-500/[0.10] via-amber-500/[0.06] to-transparent p-6 sm:p-8 shadow-[0_28px_60px_-24px_rgba(250,204,21,0.35)]"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-yellow-500/20 border border-yellow-500/45 flex items-center justify-center shrink-0">
              <Sparkles className="w-6 h-6 text-yellow-300" strokeWidth={1.8} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">
                One-time setup
              </p>
              <h2 className="mt-1 font-['Outfit',_Inter,_sans-serif] text-[19px] sm:text-[21px] font-semibold tracking-tight text-white">
                45 days free · $299/month afterwards
              </h2>
              <p className="mt-2 text-[13.5px] text-white/65 leading-relaxed">
                We reserve your card so your restaurant stays live after day 45. Nothing is charged today. You can cancel anytime from Settings before your trial ends.
              </p>
            </div>
          </div>

          <ul className="mt-6 grid sm:grid-cols-2 gap-y-2.5 gap-x-5 text-[13.5px] text-white/85">
            {[
              { Icon: Check,       text: '45 days completely free' },
              { Icon: ShieldCheck, text: 'Cancel anytime, no penalty' },
              { Icon: Clock,       text: 'Return to onboarding automatically' },
              { Icon: Sparkles,    text: 'Unlocks setup + publish' },
            ].map((b, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="mt-0.5 w-5 h-5 rounded-md bg-yellow-500/15 border border-yellow-500/35 flex items-center justify-center shrink-0">
                  <b.Icon className="w-3 h-3 text-yellow-300" strokeWidth={2.4} />
                </span>
                <span>{b.text}</span>
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={onActivateTrial}
            disabled={starting || checking}
            data-testid="onboarding-trial-activate-btn"
            className="mt-7 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 min-h-[52px] rounded-2xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[14.5px] tracking-tight disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-[0_18px_40px_-16px_rgba(250,204,21,0.55)] hover:-translate-y-0.5"
          >
            {starting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            {starting ? 'Opening Stripe…' : 'Activate 45-Day Free Trial'}
            {!starting && <ArrowRight className="w-4 h-4" />}
          </button>

          <p className="mt-3 text-center text-[11.5px] text-white/45">
            Secure payment powered by Stripe. Your card is not charged during the free trial.
          </p>
        </div>
      </div>
    </OnboardingShell>
  );
};

export default OnboardingTrialPage;
